import React from 'react';
import { Box, ButtonBase, Typography } from '@mui/material';
import MailOutlineIcon from '@mui/icons-material/MailOutline';
import SmsOutlinedIcon from '@mui/icons-material/SmsOutlined';
import RadioButtonCheckedIcon from '@mui/icons-material/RadioButtonChecked';
import RadioButtonUncheckedIcon from '@mui/icons-material/RadioButtonUnchecked';
import SettingsCard from './SettingsCard';

type CommunicationPreferenceCardProps = {
  email?: string;
  phoneNumber?: string;
  smsPreferred?: boolean;
  // Resolves when persisted; rejects on failure so the card can revert.
  onChange: (smsPreferred: boolean) => Promise<void>;
};

type OptionProps = {
  icon: React.ReactNode;
  label: string;
  subtitle: string;
  selected: boolean;
  disabled?: boolean;
  onClick: () => void;
};

function PreferenceOption({
  icon, label, subtitle, selected, disabled, onClick,
}: OptionProps) {
  return (
    <ButtonBase
      onClick={onClick}
      disabled={disabled}
      sx={{
        flex: { md: 1 },
        width: { xs: '100%', md: 'auto' },
        justifyContent: 'flex-start',
        textAlign: 'left',
        gap: 1.5,
        p: { xs: '14px 16px', md: '16px 18px' },
        minHeight: 56,
        borderRadius: '12px',
        bgcolor: selected ? 'primary.50' : '#fff',
        border: selected ? '2px solid' : '1px solid',
        borderColor: selected ? 'primary.main' : 'grey.300',
        opacity: disabled ? 0.6 : 1,
        transition: 'border-color .15s, background-color .15s',
      }}
    >
      <Box sx={{ color: selected ? 'primary.main' : 'text.secondary', display: 'flex', flex: 'none' }}>
        {icon}
      </Box>
      <Box sx={{ display: 'flex', flexDirection: 'column', minWidth: 0 }}>
        <Typography sx={{ fontWeight: 600, fontSize: 15, color: selected ? 'primary.main' : 'rgba(0,0,0,0.87)' }}>
          {label}
        </Typography>
        <Typography
          sx={{
            fontSize: 12.5,
            color: 'text.secondary',
            whiteSpace: 'nowrap',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
          }}
        >
          {subtitle}
        </Typography>
      </Box>
      <Box sx={{ ml: 'auto', display: 'flex', color: selected ? 'primary.main' : 'grey.400' }}>
        {selected ? <RadioButtonCheckedIcon /> : <RadioButtonUncheckedIcon />}
      </Box>
    </ButtonBase>
  );
}

function CommunicationPreferenceCard({
  email, phoneNumber, smsPreferred, onChange,
}: CommunicationPreferenceCardProps) {
  const [pref, setPref] = React.useState<boolean>(Boolean(smsPreferred));
  const [busy, setBusy] = React.useState(false);
  const hasPhone = Boolean(phoneNumber);

  React.useEffect(() => {
    setPref(Boolean(smsPreferred));
  }, [smsPreferred]);

  const select = async (sms: boolean) => {
    if (busy || sms === pref) return;
    setPref(sms); // optimistic
    setBusy(true);
    try {
      await onChange(sms);
    } catch {
      setPref(!sms); // revert on failure
    } finally {
      setBusy(false);
    }
  };

  return (
    <SettingsCard
      title="Communication Preference"
      subtitle="Choose how you'd like to hear from us about your pet's progress and reminders."
    >
      <Box sx={{ display: 'flex', flexDirection: { xs: 'column', md: 'row' }, gap: 1.5 }}>
        <PreferenceOption
          icon={<MailOutlineIcon />}
          label="Email"
          subtitle={email || 'Email address required'}
          selected={!pref}
          disabled={busy}
          onClick={() => select(false)}
        />
        <PreferenceOption
          icon={<SmsOutlinedIcon />}
          label="SMS"
          subtitle={hasPhone ? (phoneNumber as string) : 'Phone number required'}
          selected={pref}
          disabled={busy || !hasPhone}
          onClick={() => select(true)}
        />
      </Box>
    </SettingsCard>
  );
}

export default CommunicationPreferenceCard;
